import { DateTime } from "luxon";
import { RootState } from "store";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Select,
  Button,
  Loader,
  ErrorMessage,
  MapPinIcon,
  SelectOption,
  PracticeStatCard,
  CalendarEventIcon,
} from "@mwi/ui";
import useTitle from "hooks/useTitle";
import useSearchQuery from "hooks/useSearchQuery";
import { setFilters } from "slices/practiceStatFilters";
import { downloadQuery, useGetPracticeStatsQuery, useGetSitesQuery } from "api";
import UserRole from "enums/UserRole";

type Inputs = {
  vet_site: string;
  period_type: string;
};

const AdminPage = (): JSX.Element => {
  useTitle("Home");

  const dispatch = useDispatch();

  const [isDownloadingStats, setIsDownloadingStats] = useState(false);

  const { user, practiceStats } = useSelector((state: RootState) => ({
    user: state.auth.user,
    practiceStats: state.practiceStatFilters,
  }));

  const { data: sites } = useGetSitesQuery();

  const periodOptions = [
    { id: "yesterday", name: "Yesterday" },
    { id: "this_week", name: "This week" },
    { id: "last_week", name: "Last week" },
    { id: "this_month", name: "This month" },
    { id: "last_month", name: "Last month" },
    { id: "last_three_months", name: "Last 3 months" },
  ];

  const { control, watch, getValues } = useForm<Inputs>({
    defaultValues: {
      vet_site: practiceStats.location_uuid,
      period_type: practiceStats.period_type,
    },
  });

  const searchParams = useMemo(
    () => ({
      "period-type": getValues("period_type"),
      "vet-site":
        sites?.find((s) => s.id === getValues("vet_site"))?.name ?? "all_sites",
    }),
    [watch("period_type"), watch("vet_site")],
  );

  useSearchQuery(searchParams);

  useEffect(() => {
    let startDate = DateTime.fromISO(practiceStats.start_date);
    let endDate = DateTime.fromISO(practiceStats.end_date);
    switch (getValues("period_type")) {
      case "yesterday":
        startDate = DateTime.now().minus({ day: 1 }).startOf("day");
        endDate = DateTime.now().minus({ day: 1 }).endOf("day");
        break;
      case "this_week":
        startDate = DateTime.now().startOf("week");
        endDate = DateTime.now();
        break;
      case "last_week":
        startDate = DateTime.now().minus({ week: 1 }).startOf("week");
        endDate = DateTime.now().minus({ week: 1 }).endOf("week");
        break;
      case "this_month":
        startDate = DateTime.now().startOf("month");
        endDate = DateTime.now();
        break;
      case "last_month":
        startDate = DateTime.now().minus({ month: 1 }).startOf("month");
        endDate = DateTime.now().minus({ month: 1 }).endOf("month");
        break;
      case "last_three_months":
        startDate = DateTime.now().minus({ months: 3 });
        endDate = DateTime.now();
        break;
    }

    dispatch(
      setFilters({
        period_type: getValues("period_type"),
        location_uuid: getValues("vet_site"),
        start_date: startDate.toISO() ?? undefined,
        end_date: endDate.toISO() ?? undefined,
      }),
    );
  }, [watch("period_type"), watch("vet_site")]);

  const {
    data: statData,
    isError,
    isLoading: loadingStats,
  } = useGetPracticeStatsQuery({
    filters: {
      start_date:
        DateTime.fromISO(practiceStats?.start_date).toISO({
          includeOffset: false,
        }) ?? "",
      end_date:
        DateTime.fromISO(practiceStats?.end_date).toISO({
          includeOffset: false,
        }) ?? "",
      location_uuid:
        practiceStats.location_uuid !== "all_sites"
          ? practiceStats.location_uuid
          : "",
    },
  });

  const siteOptions = useMemo(() => {
    const allSitesOption = { id: "all_sites", name: "All" };
    return sites ? [allSitesOption, ...sites] : [allSitesOption];
  }, [sites]);

  const downloadStats = async () => {
    setIsDownloadingStats(true);
    await downloadQuery({
      url: "/practices/stats/download",
      params: {
        start_date: practiceStats.start_date,
        end_date: practiceStats.end_date,
        location_uuid:
          practiceStats.location_uuid !== "all_sites"
            ? practiceStats.location_uuid
            : "",
      },
      filename: "practice_stats_export.csv",
    });
    setIsDownloadingStats(false);
  };

  return (
    <>
      <div className="mb-12">
        <h1 className="text-[28px] leading-[34px] tablet:text-5xl tablet:leading-[58px] capitalize">
          {`Welcome ${user?.first_name ?? ""}`}
        </h1>
      </div>

      {isError && (
        <div className="flex justify-center w-full py-10">
          <ErrorMessage message="We're unable to display any statistics at the moment." />
        </div>
      )}

      {loadingStats && (
        <div className="flex justify-center w-full py-10">
          <Loader />
        </div>
      )}

      {!isError && !loadingStats && (
        <>
          <div className="flex justify-between w-full mb-12">
            <div className="flex flex-wrap w-full gap-6 tablet:flex-nowrap">
              <div className="w-full mobile:max-w-[200px] desktop:max-w-[300px]">
                <Select
                  name="period_type"
                  size="small"
                  control={control}
                  placeholder="Select a period"
                  prefixIcon={<CalendarEventIcon />}
                >
                  {periodOptions?.map((p) => (
                    <SelectOption key={p.id} value={p.id}>
                      {p.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>
              {user?.role !== UserRole.MWI_ADMIN && (
                <div className="w-full mobile:max-w-[200px] desktop:max-w-[300px]">
                  <Select
                    name="vet_site"
                    size="small"
                    control={control}
                    prefixIcon={<MapPinIcon />}
                    placeholder="Select a vet site"
                  >
                    {siteOptions?.map((s) => (
                      <SelectOption key={s.id} value={s.id}>
                        {s.name}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            <div className="w-full max-w-[174px] flex items-center">
              <Button
                size="small"
                className="whitespace-nowrap h-[45px]"
                loading={isDownloadingStats}
                onClick={downloadStats}
                block
              >
                Export selected view
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 tablet:grid-cols-2 desktop:grid-cols-3">
            {statData &&
              statData?.stats?.length > 0 &&
              statData.stats.map((stat) => (
                <div key={stat.key}>
                  <PracticeStatCard
                    type={stat.key}
                    statValue={stat.stats_value}
                    subHeading={stat.sub_heading}
                  />
                </div>
              ))}
          </div>
        </>
      )}
    </>
  );
};

export default AdminPage;
